
import React, { useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import styles from "./crewWeekGrid.module.css";
import moment from "moment";
import { Flex, Button } from "antd";
import { Text } from "ui/Typography";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import EventBlock from "./EventBlock";
import { getEventColor, getEventBorderColor, getJournalIcon } from "pagesV2/pro/Calendar/utils";
import BookingDetailsModal from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/Modals/BookingDetails";
import { useRefreshCalendarBookings } from "pagesV2/pro/Calendar/useRefreshCalendarBookings";
import { useCalendarJournalNoteDates } from "pagesV2/pro/Calendar/hooks/useCalendarSelectors";
import Journal from "pagesV2/pro/Calendar/Modals/Journal";
import IconWithBg from "ui/IconWithBg";
import useActiveGuide from "hooks/useActiveClient";
import { usePermissions } from "hooks/usePermissions";
import { MODULES, ACTIONS } from "utils/permissions";

const isEventForCrewAndDay = (ev, crew, day) =>
  moment(ev.date).isSame(day, "day") &&
  (ev.assigned_crew || []).some((c) => c.profile_id === crew.id || c.id === crew.id || c.name === crew.name)

const buildCrewDayMap = (crew, calendarDays, events) => {
  const dayMap = {}
  calendarDays.forEach((day) => {
    const dayStr = day.format("YYYY-MM-DD")
    dayMap[dayStr] = events.filter((ev) => isEventForCrewAndDay(ev, crew, day)).map(formatEventLabels)
  })
  return dayMap
}

const formatEventLabels = (ev) => {
  const startMoment = ev.start ? moment(ev.start, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
  const endMoment = ev.end ? moment(ev.end, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
  return {
    ...ev,
    startLabel: startMoment?.isValid() ? startMoment.format("h:mm A") : ev.start,
    endLabel: endMoment?.isValid() ? endMoment.format("h:mm A") : ev.end,
  }
}

// CrewWeekGrid: Crew as rows, days as columns, table layout
const CrewWeekGrid = ({ calendarDays, events, crewIds, roleIds }) => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [visibleCounts, setVisibleCounts] = useState({});
  const { t } = useTranslation();
  const MAX_VISIBLE = 2;

  const getVisibleCount = useCallback((key) => visibleCounts[key] ?? MAX_VISIBLE, [visibleCounts]);
  const showMore = useCallback((key) => setVisibleCounts((prev) => ({ ...prev, [key]: (prev[key] ?? MAX_VISIBLE) + MAX_VISIBLE })), []);
  const showLess = useCallback((key) => setVisibleCounts((prev) => ({ ...prev, [key]: MAX_VISIBLE })), []);
  const journalNoteDates = useCalendarJournalNoteDates();
  const { isActive: active } = useActiveGuide();
  const { hasPermission } = usePermissions();
  const canViewJournal = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL);
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL);
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL);
  const weekStartDate = calendarDays?.[0]?.format("YYYY-MM-DD");
  const weekEndDate = calendarDays?.at(-1)?.format("YYYY-MM-DD");

  const calendarCrew = useSelector((state) => state.staff?.calendarCrew || []);

  const crewMembers = React.useMemo(() => {
    const active = calendarCrew
      .filter((c) => c.status?.toLowerCase() === "active")
      .map((c) => ({ id: c.id, name: c.name }))

    // Collect crew IDs from assigned_crew in events (for role filter)
    const assignedCrewIds = new Set()
    if (roleIds?.length > 0) {
      events.forEach((ev) => {
        (ev.assigned_crew || []).forEach((c) => {
          if (c.profile_id) assignedCrewIds.add(c.profile_id)
          if (c.id) assignedCrewIds.add(c.id)
        })
      })
    }

    // Both crew + role filter — merge selected crew with crew from events (deduplicated)
    if (crewIds?.length > 0 && roleIds?.length > 0) {
      const mergedIds = new Set([...crewIds.filter((id) => id !== "unassigned"), ...assignedCrewIds])
      return active.filter((c) => mergedIds.has(c.id))
    }

    // Crew member filter only — show selected crew
    if (crewIds?.length > 0) return active.filter((c) => crewIds.includes(c.id))

    // Role filter only — show crew from assigned events
    if (roleIds?.length > 0) {
      return active.filter((c) => assignedCrewIds.has(c.id))
    }

    // No filter — show all active crew
    return active
  }, [calendarCrew, crewIds, events, roleIds]);

  // Show unassigned row only when no filter is active OR when "unassigned" is explicitly selected
  // Also hide if role filter is active but there are no unassigned events at all
  const hasAnyUnassignedEvents = useMemo(() =>
    calendarDays.some((day) =>
      events.some((ev) => moment(ev.date).isSame(day, "day") && (!ev.assigned_crew || ev.assigned_crew.length === 0))
    ),
    [calendarDays, events]
  );
  const showUnassigned = crewIds?.includes("unassigned") || (!crewIds?.length && (!roleIds?.length || hasAnyUnassignedEvents));
  const showNameColumn = true;

  const eventsByCrewAndDay = useMemo(() => {
    const map = {};
    crewMembers.forEach((crew) => {
      map[crew.id] = buildCrewDayMap(crew, calendarDays, events);
    });
    return map;
  }, [crewMembers, calendarDays, events]);

  // Build unassigned events map: events with empty assigned_crew
  const unassignedEventsByDay = useMemo(() => {
    const dayMap = {};
    calendarDays.forEach((day) => {
      const dayStr = day.format("YYYY-MM-DD");
      dayMap[dayStr] = events
        .filter((ev) => moment(ev.date).isSame(day, "day") && (!ev.assigned_crew || ev.assigned_crew.length === 0))
        .map(formatEventLabels);
    });
    return dayMap;
  }, [calendarDays, events]);

  const refreshCalendarBookings = useRefreshCalendarBookings()
  const handleBookingCancel = () => {
    setIsBookingModalOpen(false);
    setSelectedBookingId(null);
  };
  const handleBookingCancelSuccess = () => { handleBookingCancel(); refreshCalendarBookings() }

  return (
    <div className={styles.crewWeekGrid}>
      <table className={styles.crewWeekTable}>
        <thead>
          <tr className={styles.crewWeekHeaderRow}>
            {showNameColumn && <th className={styles.crewWeekHeaderCell}>Crew Member</th>}
            {calendarDays.map((day) => {
              const dayStr = day.format("YYYY-MM-DD");
              const hasJournalNote = journalNoteDates?.includes(dayStr);
              const hasJournalPermission = hasJournalNote ? canViewJournal || canEditJournal : canCreateJournal;
              return (
                <th key={dayStr} className={styles.crewWeekHeaderCell}>
                  <Flex vertical align="center" gap={2}>
                    <Text fw="500">{day.format("ddd D")}</Text>
                  </Flex>
                  {hasJournalPermission && (
                    <Flex justify="center" className="mt-xxs">
                      <IconWithBg
                        disabled={!active}
                        icon={getJournalIcon(journalNoteDates, dayStr)}
                        onClick={active ? () => { setSelectedDate(dayStr); setIsJournalModalOpen(true); } : undefined}
                      />
                    </Flex>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {crewMembers.length === 0 && !showUnassigned ? (
            [0, 1, 2, 3, 4].map((i) => (
              <tr key={`empty-${i}`} className={styles.crewWeekRow}>
                {showNameColumn && <td className={styles.crewWeekCrewCell} />}
                {calendarDays.map((day) => (
                  <td key={day.format("YYYY-MM-DD")} className={styles.crewWeekEventCell} />
                ))}
              </tr>
            ))
          ) : (
            <>
              {showUnassigned && (
                <tr key="unassigned" className={styles.crewWeekRow}>
                  {showNameColumn && (
                    <td className={styles.crewWeekCrewCell}>
                      <Text>Unassigned</Text>
                    </td>
                  )}
                  {calendarDays.map((day) => {
                    const dayStr = day.format("YYYY-MM-DD");
                    const cellEvents = unassignedEventsByDay[dayStr] || [];
                    const cellKey = `unassigned-${dayStr}`;
                    const visibleCount = getVisibleCount(cellKey);
                    const visibleEvents = cellEvents.slice(0, visibleCount);
                    const allVisible = visibleCount >= cellEvents.length;
                    const hasMore = !allVisible && cellEvents.length > MAX_VISIBLE;
                    const canShowLess = allVisible && cellEvents.length > MAX_VISIBLE;
                    return (
                      <td key={dayStr} className={styles.crewWeekEventCell}>
                        <div className={styles.crewWeekEventCellInner}>
                          {visibleEvents.map((ev) => (
                            <EventBlock
                              key={ev.id ?? (ev.title + ev.start)}
                              event={ev}
                              eventColor={getEventColor(ev.payment_status)}
                              eventBorderColor={getEventBorderColor(ev.payment_status)}
                              setIsBookingModalOpen={setIsBookingModalOpen}
                              setSelectedBookingId={setSelectedBookingId}
                              compact
                            />
                          ))}
                          {hasMore && (
                            <Button type="link" size="small" onClick={() => showMore(cellKey)}>
                              {t("calendar.view_more")}
                            </Button>
                          )}
                          {canShowLess && (
                            <Button type="link" size="small" onClick={() => showLess(cellKey)}>
                              Read less
                            </Button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              )}
              {crewMembers.map((crew) => (
                <tr key={crew.id} className={styles.crewWeekRow}>
                  {showNameColumn && (
                    <td className={styles.crewWeekCrewCell}>
                      <Text>{crew.name}</Text>
                    </td>
                  )}
                  {calendarDays.map((day) => {
                    const dayStr = day.format("YYYY-MM-DD");
                    const cellEvents = eventsByCrewAndDay[crew.id]?.[dayStr] || [];
                    const cellKey = `${crew.id}-${dayStr}`;
                    const visibleCount = getVisibleCount(cellKey);
                    const visibleEvents = cellEvents.slice(0, visibleCount);
                    const hasMore = cellEvents.length > visibleCount;
                    const canShowLess = visibleCount > MAX_VISIBLE && cellEvents.length > MAX_VISIBLE;
                    return (
                      <td key={dayStr} className={styles.crewWeekEventCell}>
                        <div className={styles.crewWeekEventCellInner}>
                          {visibleEvents.map((ev) => (
                            <EventBlock
                              key={ev.id ?? (ev.title + ev.start)}
                              event={ev}
                              eventColor={getEventColor(ev.payment_status)}
                              eventBorderColor={getEventBorderColor(ev.payment_status)}
                              setIsBookingModalOpen={setIsBookingModalOpen}
                              setSelectedBookingId={setSelectedBookingId}
                              compact
                            />
                          ))}
                          {hasMore && (
                            <Button type="link" size="small" onClick={() => showMore(cellKey)}>
                              {t("calendar.view_more")}
                            </Button>
                          )}
                          {canShowLess && (
                            <Button type="link" size="small" onClick={() => showLess(cellKey)}>
                              Read less
                            </Button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {crewMembers.length === 0 && !showUnassigned && [0, 1, 2, 3].map((i) => (
                <tr key={`placeholder-${i}`} className={styles.crewWeekRow}>
                  {showNameColumn && <td className={styles.crewWeekCrewCell} />}
                  {calendarDays.map((day) => (
                    <td key={day.format("YYYY-MM-DD")} className={styles.crewWeekEventCell} />
                  ))}
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>

      {isBookingModalOpen && (
        <BookingDetailsModal
          open={isBookingModalOpen}
          handleOnCancel={handleBookingCancel}
          bookingUuid={selectedBookingId}
          onCancelSuccess={handleBookingCancelSuccess}
        />
      )}
      {isJournalModalOpen && (
        <Journal
          open={isJournalModalOpen}
          setOpen={setIsJournalModalOpen}
          onCancel={() => setIsJournalModalOpen(false)}
          selectedDate={selectedDate}
          startDate={weekStartDate}
          endDate={weekEndDate}
          range="week"
        />
      )}
    </div>
  );
};

CrewWeekGrid.propTypes = {
  calendarDays: PropTypes.array.isRequired,
  events: PropTypes.array.isRequired,
  crewIds: PropTypes.array,
  roleIds: PropTypes.array,
};

export default CrewWeekGrid;
